import React, { MutableRefObject, useState } from "react";
import moment from "moment";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import { TrendsVisibilityToggle } from "../TrendsVisibilityToggle/TrendsVisibilityToggle";
import {
  SETUP_MODE_QUERY_PARAM_KEY,
  SetupModes,
} from "../../panes/InsightsDashboardSetupPane/InsightsDashboardSetupPane";
import { setSearchParam } from "src/utils/methods";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { setSelectedDashboard } from "src/redux";
import { openAIChatWithContext } from "src/redux/ai-chat/aiChatActions";
import { ReactComponent as AIStarsIcon } from "src/images/ai-stars.svg";
import { ReactComponent as EditIcon } from "src/images/edit.svg";
import { ReactComponent as DuplicateIcon } from "src/images/duplicate.svg";
import { ReactComponent as PdfIcon } from "src/images/pdf-alt.svg";
import { SaveDashboardModal } from "../SocialInsightsDashboardManagement/modals/SaveDashboardModal/SaveDashboardModal";
import { RenameDashboardModal } from "../SocialInsightsDashboardManagement/modals/RenameDashboardModal/RenameDashboardModal";
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from "src/app/components/Breadcrumbs/Breadcrumbs";
import { SOCIAL_LABELS } from "src/types/generic";
import { WorkspacePublicationsType } from "src/redux/insights-dashboard/types";

import "./InsightsDashboardToolbar.scss";

interface Props {
  blockDebounce: MutableRefObject<boolean>;
  downloadPdf: () => void;
  canDownloadPdf: boolean;
}

const InsightsDashboardToolbar = (props: Props) => {
  const { downloadPdf, canDownloadPdf } = props;

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { selectedDashboard, setup, dateRange, dateFrom, dateTo } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const dateRangeLabel =
    dateRange?.value === "custom" && dateFrom && dateTo
      ? `${moment(dateFrom).format("DD.MM.YYYY")} - ${moment(dateTo).format(
          "DD.MM.YYYY",
        )}`
      : dateRange?.label;

  const goToDashboardsList = () => {
    dispatch(setSelectedDashboard(null));
    history.push(
      getRoute(ROUTES.SOCIAL_INSIGHTS, {
        wsWorkspaceUuid: activeWorkspaceUuid,
      }),
    );
  };

  return (
    <>
      <div className="insights-dashboard__toolbar-header">
        <Breadcrumbs>
          <BreadcrumbsItem
            text={
              <IDHFormattedMessage
                id="front_social_insights"
                defaultMessage="Social Insights"
              />
            }
            to={getRoute(ROUTES.SOCIAL_INSIGHTS, {
              wsWorkspaceUuid: activeWorkspaceUuid,
            })}
            onClick={goToDashboardsList}
          />
          {selectedDashboard && (
            <BreadcrumbsItem text={selectedDashboard.name} to="" disabled />
          )}
        </Breadcrumbs>

        <div className="insights-dashboard__toolbar-title-row">
          <h1 className="insights-dashboard__toolbar-title">
            {selectedDashboard?.name}

            <button
              type="button"
              className="insights-dashboard__toolbar-title-edit"
              data-qa="edit-dashboard-name"
              onClick={() => setShowRenameModal(true)}
            >
              <EditIcon />
            </button>
          </h1>

          <div className="insights-dashboard__toolbar-actions">
            <Button
              variant="bare"
              textWithIcon
              dataQa="dashboard-setup"
              onClick={() =>
                setSearchParam({
                  history,
                  key: SETUP_MODE_QUERY_PARAM_KEY,
                  value: SetupModes.Edit,
                })
              }
            >
              <FilterIcon />
              <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
            </Button>

            <Button
              variant="bare"
              textWithIcon
              dataQa="duplicate-dashboard"
              onClick={() => setShowDuplicateModal(true)}
            >
              <DuplicateIcon />
              <IDHFormattedMessage
                id="ws_duplicate"
                defaultMessage="Duplicate"
              />
            </Button>

            {canDownloadPdf && (
              <Button
                variant="bare"
                textWithIcon
                dataQa="download-dashboard-pdf"
                onClick={downloadPdf}
              >
                <PdfIcon />
                <IDHFormattedMessage
                  id="ws_download_pdf"
                  defaultMessage="Download PDF"
                />
              </Button>
            )}

            <Button
              variant="bare"
              textWithIcon
              dataQa="discuss-with-ai"
              onClick={() =>
                selectedDashboard &&
                dispatch(
                  openAIChatWithContext({
                    type: "selectedSocialInsightsDashboards",
                    data: [
                      {
                        uuid: selectedDashboard.uuid,
                        title: selectedDashboard.name,
                      },
                    ],
                  }),
                )
              }
            >
              <AIStarsIcon />
              <IDHFormattedMessage
                id="ws_discuss_with_ai_assistant"
                defaultMessage="Discuss with AI Assistant"
              />
            </Button>

            <span className="insights-dashboard__toolbar-divider" />

            <TrendsVisibilityToggle />
          </div>
        </div>
      </div>

      {selectedDashboard && (
        <div className="insights-dashboard__toolbar-filter-summary">
          {setup.hashtags.map((hashtag) => (
            <span
              key={`hashtag-${hashtag}`}
              className="insights-dashboard__toolbar-filter-badge"
            >
              <IDHFormattedMessage id="ws_hashtag" defaultMessage="Hashtag" />
              :&nbsp;
              {hashtag}
            </span>
          ))}

          {setup.mentions.map((mention) => (
            <span
              key={`mention-${mention}`}
              className="insights-dashboard__toolbar-filter-badge"
            >
              <IDHFormattedMessage id="ws_mention" defaultMessage="Mention" />
              :&nbsp;
              {mention}
            </span>
          ))}

          {setup.platforms.map((platform) => (
            <span
              key={`platform-${platform}`}
              className="insights-dashboard__toolbar-filter-badge"
            >
              <IDHFormattedMessage
                id="ws_social_platform"
                defaultMessage="Social platform"
              />
              :&nbsp;
              {SOCIAL_LABELS[platform] || platform}
            </span>
          ))}

          {setup.countries.map((country) => (
            <span
              key={`country-${country.value}`}
              className="insights-dashboard__toolbar-filter-badge"
            >
              <IDHFormattedMessage id="ws_country" defaultMessage="Country" />
              :&nbsp;
              {country.label}
            </span>
          ))}

          {dateRange && (
            <span className="insights-dashboard__toolbar-filter-badge">
              <IDHFormattedMessage
                id="ws_time_range"
                defaultMessage="Time range"
              />
              :&nbsp;
              {dateRangeLabel}
            </span>
          )}

          <span className="insights-dashboard__toolbar-filter-badge">
            <IDHFormattedMessage
              id="ws_insight_scope"
              defaultMessage="Insight scope"
            />
            :&nbsp;
            {setup.workspacePublicationsType ===
            WorkspacePublicationsType.WorkspaceOnly ? (
              <IDHFormattedMessage
                id="insight_scope_my_workspace"
                defaultMessage="My workspace"
              />
            ) : (
              <IDHFormattedMessage
                id="insight_scope_market_view"
                defaultMessage="Market view"
              />
            )}
          </span>
        </div>
      )}

      {showDuplicateModal && (
        <SaveDashboardModal
          onClose={() => setShowDuplicateModal(false)}
          isDuplicate
        />
      )}

      {showRenameModal && selectedDashboard && (
        <RenameDashboardModal
          data={selectedDashboard}
          onClose={() => setShowRenameModal(false)}
        />
      )}
    </>
  );
};

export default InsightsDashboardToolbar;
